import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Siren, Route, FileWarning, Ghost, Clock, Hourglass, IndianRupee, ShieldCheck } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  AggHeader, ResetSampleButton, Kpi, Panel, useSort, SortTh, TH_ROW, TD, inr, inrCompact, num, fmtDate, hoursLabel,
  StatusPill, ExceptionPill, Pill, TableFilter, textMatch, FieldSelect, useAggMeta, EXC_LABEL,
} from "./aggKit";
import { ShipmentDetailSheet } from "./AggShipmentsPage";

const CLAIM_STYLE = {
  filed: "bg-sky-500/15 text-sky-700 dark:text-sky-300 border-sky-500/30",
  under_review: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30",
  approved: "bg-indigo-500/15 text-indigo-700 dark:text-indigo-300 border-indigo-500/30",
  rejected: "bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/30",
  settled: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30",
};
const CLAIM_LABEL = { filed: "Filed", under_review: "Under review", approved: "Approved", rejected: "Rejected", settled: "Settled" };

function RerouteDialog({ item, onClose, meta, onDone }) {
  const [carrier, setCarrier] = useState("");
  const [city, setCity] = useState("");
  const [pin, setPin] = useState("");
  const [addr, setAddr] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (item) { setCarrier(item.carrier_id); setCity(""); setPin(""); setAddr(""); setReason(item.exception?.type === "lost" ? "Parcel untraceable with current carrier – re-dispatching replacement" : "SLA breach – moving to faster lane"); }
  }, [item]);
  const save = async () => {
    if (!reason.trim()) { toast.error("Give a reason"); return; }
    setBusy(true);
    try {
      const r = await api.post(`/agg/shipments/${item.id}/reroute`, { new_carrier_id: carrier, new_dest_city: city || undefined, new_dest_pincode: pin || undefined, new_address: addr || undefined, reason });
      toast.success(`Re-routed · new AWB ${r.data.awb} · due ${fmtDate(r.data.expected_delivery_at)}`);
      onDone(); onClose();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none max-w-lg" data-testid="reroute-dialog">
        <DialogHeader>
          <DialogTitle>Manual re-route · {item?.awb}</DialogTitle>
          <DialogDescription>Move the parcel to another 3PL and/or a corrected address. The SLA clock restarts. This is recorded in the app only; it does not book with the carrier.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1"><Label className="text-xs">Carrier</Label>
            <FieldSelect testid="rr-carrier" className="w-full" value={carrier} onChange={setCarrier}>{meta?.carriers.map((c) => <option key={c.id} value={c.id}>{c.name}{c.id === item?.carrier_id ? " (current)" : ""}</option>)}</FieldSelect>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1"><Label className="text-xs">New destination city (optional)</Label>
              <FieldSelect testid="rr-city" className="w-full" value={city} onChange={setCity}><option value="">Keep {item?.dest_city}</option>{meta?.destinations.map((d) => <option key={d.city} value={d.city}>{d.city}</option>)}</FieldSelect>
            </div>
            <div className="space-y-1"><Label className="text-xs">Pincode</Label><Input className="rounded-none h-9" value={pin} onChange={(e) => setPin(e.target.value)} data-testid="rr-pin" placeholder={item?.dest_pincode} /></div>
          </div>
          <div className="space-y-1"><Label className="text-xs">Corrected address (optional)</Label><Input className="rounded-none h-9" value={addr} onChange={(e) => setAddr(e.target.value)} data-testid="rr-addr" /></div>
          <div className="space-y-1"><Label className="text-xs">Reason *</Label><Textarea className="rounded-none" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="rr-reason" /></div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" className="rounded-none" onClick={onClose}>Cancel</Button>
            <Button className="rounded-none" disabled={busy} onClick={save} data-testid="rr-save">{busy ? "Re-routing…" : "Re-route parcel"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ClaimDialog({ item, onClose, onDone }) {
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [insurer, setInsurer] = useState("Carrier liability");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (item) { setAmount(item.declared_value); setReason(item.exception ? `${EXC_LABEL[item.exception.type]} – last scan ${hoursLabel(item.exception.hours_since_scan)} ago` : ""); setInsurer("Carrier liability"); }
  }, [item]);
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/agg/shipments/${item.id}/claim`, { amount: Number(amount), reason, insurer });
      toast.success(`Claim ${r.data.claim_no} filed for ${inr(r.data.amount)}`);
      onDone(); onClose();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!item} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-none max-w-md" data-testid="claim-dialog">
        <DialogHeader>
          <DialogTitle>Raise insurance claim · {item?.awb}</DialogTitle>
          <DialogDescription>{item?.carrier_name} · declared value {inr(item?.declared_value)}. The claim is tracked in-app; send the claim pack to the insurer or carrier separately.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1"><Label className="text-xs">Claim amount (₹)</Label><Input type="number" min="1" className="rounded-none h-9" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="cl-amount" /></div>
            <div className="space-y-1"><Label className="text-xs">Claim against</Label>
              <FieldSelect testid="cl-insurer" className="w-full" value={insurer} onChange={setInsurer}><option>Carrier liability</option><option>Transit insurance policy</option><option>Franchise hub liability</option></FieldSelect>
            </div>
          </div>
          <div className="space-y-1"><Label className="text-xs">Reason *</Label><Textarea className="rounded-none" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} data-testid="cl-reason" /></div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" className="rounded-none" onClick={onClose}>Cancel</Button>
            <Button className="rounded-none" disabled={busy || !reason.trim() || !(Number(amount) > 0)} onClick={save} data-testid="cl-save">{busy ? "Filing…" : "File claim"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function AggExceptionsPage() {
  const meta = useAggMeta();
  const [d, setD] = useState(null);
  const [tick, setTick] = useState(0);
  const [tab, setTab] = useState("alerts");
  const [type, setType] = useState("");
  const [carrier, setCarrier] = useState("");
  const [q, setQ] = useState("");
  const [rr, setRr] = useState(null);
  const [cl, setCl] = useState(null);
  const [detailId, setDetailId] = useState(null);

  const load = useCallback(() => {
    api.get("/agg/exceptions").then((r) => setD(r.data)).catch((e) => toast.error(apiErr(e)));
  }, []);
  useEffect(() => { load(); }, [load, tick]);
  const refresh = () => setTick((t) => t + 1);

  const items = useMemo(() => (d?.items || []).filter((x) => (!type || x.exception.type === type) && (!carrier || x.carrier_id === carrier) &&
    textMatch(x, q, ["awb", "order_id", "merchant_name", "carrier_name", "dest_city", "customer_name"]))
    .map((x) => ({ ...x, _sev: { critical: 3, high: 2, medium: 1 }[x.exception.severity], _scan: x.exception.hours_since_scan, _breach: x.exception.sla_breach_hours })), [d, type, carrier, q]);
  const es = useSort(items, "_sev");
  const claims = useMemo(() => (d?.claims || []).filter((c) => textMatch({ ...c, claim_no: c.claim.claim_no }, q, ["awb", "claim_no", "merchant_name", "carrier_name"]))
    .map((c) => ({ ...c, _no: c.claim.claim_no, _amt: c.claim.amount, _status: c.claim.status, _filed: c.claim.filed_at, _settled: c.claim.settled_amount })), [d, q]);
  const cs = useSort(claims, "_filed");

  const markLost = async (x) => {
    if (!window.confirm(`Declare ${x.awb} lost? You can then raise an insurance claim.`)) return;
    try { await api.post(`/agg/shipments/${x.id}/mark-lost`); toast.success("Marked as lost"); refresh(); } catch (e) { toast.error(apiErr(e)); }
  };
  const updClaim = async (c, status) => {
    let settled;
    if (status === "settled") {
      const v = window.prompt(`Settled amount for ${c.claim.claim_no} (₹):`, c.claim.amount);
      if (v === null) return;
      settled = Number(v);
    }
    try { await api.patch(`/agg/shipments/${c.id}/claim`, { status, settled_amount: settled }); toast.success(`Claim ${CLAIM_LABEL[status].toLowerCase()}`); refresh(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const s = d?.summary;
  return (
    <div className="space-y-4" data-testid="agg-exceptions-page">
      <AggHeader icon={Siren} title="Exceptions & SLA Breach Alerts"
        subtitle="Stuck, delayed and lost parcels, with manual re-route and insurance claim actions."
        actions={<ResetSampleButton onDone={refresh} />} />
      {!d ? <div className="text-sm text-muted-foreground p-6">Loading alerts…</div> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <Kpi testid="ex-kpi-lost" label="Lost / untraceable" icon={Ghost} accent="text-rose-500" value={num(s.lost)} sub="no scan for 7+ days" />
            <Kpi testid="ex-kpi-stuck" label="Stuck" icon={Hourglass} accent="text-orange-500" value={num(s.stuck)} sub="no scan for 72+ hours" />
            <Kpi testid="ex-kpi-delayed" label="SLA breached" icon={Clock} accent="text-amber-500" value={num(s.delayed)} sub="past promised delivery date" />
            <Kpi testid="ex-kpi-value" label="Value at risk" icon={IndianRupee} accent="text-rose-500" value={inrCompact(s.value_at_risk)} sub="declared value, lost + stuck" />
            <Kpi testid="ex-kpi-claims" label="Open insurance claims" icon={ShieldCheck} accent="text-sky-500" value={num(s.claims_open)} sub={`${inrCompact(s.claims_value)} claimed in total`} />
          </div>

          <div className="flex items-center gap-1 flex-wrap">
            {[["alerts", `Alerts (${s.total})`], ["claims", `Insurance claims (${d.claims.length})`]].map(([k, l]) => (
              <button key={k} onClick={() => setTab(k)} data-testid={`ex-tab-${k}`} className={`px-3 py-2 text-xs font-bold uppercase tracking-wide border ${tab === k ? "bg-[#0B1220] text-white border-[#0B1220] dark:bg-primary dark:border-primary" : "border-border hover:bg-muted"}`}>{l}</button>
            ))}
            <div className="flex-1" />
            {tab === "alerts" && <>
              <FieldSelect testid="ex-type" value={type} onChange={setType} className="h-8 text-xs"><option value="">All types</option><option value="lost">Lost</option><option value="stuck">Stuck</option><option value="delayed">SLA breached</option></FieldSelect>
              <FieldSelect testid="ex-carrier" value={carrier} onChange={setCarrier} className="h-8 text-xs"><option value="">All carriers</option>{meta?.carriers.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</FieldSelect>
            </>}
            <TableFilter testid="ex-search" value={q} onChange={setQ} placeholder="Filter AWB, merchant…" />
          </div>

          {tab === "alerts" ? (
            <Panel testid="ex-table" bodyClass="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className={TH_ROW}>
                  <tr>
                    <SortTh k="_sev" label="Severity" sort={es} />
                    <SortTh k="awb" label="AWB" sort={es} />
                    <SortTh k="merchant_name" label="Merchant" sort={es} />
                    <SortTh k="carrier_name" label="Carrier" sort={es} />
                    <SortTh k="dest_city" label="Route" sort={es} />
                    <SortTh k="status" label="Stage" sort={es} />
                    <th className="px-3 py-2 text-left">Last event</th>
                    <SortTh k="_scan" label="Since scan" sort={es} align="right" />
                    <SortTh k="_breach" label="SLA over by" sort={es} align="right" />
                    <SortTh k="declared_value" label="Value" sort={es} align="right" />
                    <th className="px-3 py-2 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {es.sorted.length === 0 && <tr><td colSpan={11} className="p-8 text-center text-muted-foreground">No open exceptions 🎉</td></tr>}
                  {es.sorted.map((x) => (
                    <tr key={x.id} className="hover:bg-muted/40" data-testid={`ex-row-${x.id}`}>
                      <td className={TD}><ExceptionPill exc={x.exception} /></td>
                      <td className={TD}><button onClick={() => setDetailId(x.id)} className="font-mono-num font-bold hover:text-primary" data-testid={`ex-open-${x.id}`}>{x.awb}</button><div className="text-[10px] text-muted-foreground">{x.order_id}</div></td>
                      <td className={`${TD} max-w-[140px] truncate`}>{x.merchant_name}</td>
                      <td className={TD}>{x.carrier_name}{x.reroutes.length > 0 && <div className="text-[10px] text-primary">re-routed ×{x.reroutes.length}</div>}</td>
                      <td className={TD}>{x.origin_city} → {x.dest_city}<div className="text-[10px] text-muted-foreground">{x.dest_region} · {x.dest_pincode}</div></td>
                      <td className={TD}><StatusPill status={x.status} /></td>
                      <td className="px-3 py-2 text-[11px] text-muted-foreground max-w-[200px] truncate" title={x.last_event}>{x.last_event}<div>{fmtDate(x.last_scan_at, true)}</div></td>
                      <td className={`${TD} text-right font-mono-num font-bold ${x._scan > 168 ? "text-rose-600 dark:text-rose-400" : x._scan > 72 ? "text-orange-600 dark:text-orange-400" : ""}`}>{hoursLabel(x._scan)}</td>
                      <td className={`${TD} text-right font-mono-num`}>{x._breach > 0 ? hoursLabel(x._breach) : "—"}</td>
                      <td className={`${TD} text-right font-mono-num`}>{inr(x.declared_value)}<div className="text-[10px] uppercase text-muted-foreground">{x.payment_mode}</div></td>
                      <td className={TD}>
                        <div className="flex gap-1">
                          <button onClick={() => setRr(x)} data-testid={`ex-reroute-${x.id}`} className="h-6 px-1.5 bg-primary text-primary-foreground text-[10px] font-bold uppercase inline-flex items-center gap-1"><Route className="h-3 w-3" /> Re-route</button>
                          {x.claim ? <Pill className={CLAIM_STYLE[x.claim.status]}>{x.claim.claim_no}</Pill> : (
                            <button onClick={() => setCl(x)} data-testid={`ex-claim-${x.id}`} className="h-6 px-1.5 border border-sky-500/40 text-sky-700 dark:text-sky-300 hover:bg-sky-500/10 text-[10px] font-bold uppercase inline-flex items-center gap-1"><FileWarning className="h-3 w-3" /> Claim</button>
                          )}
                          {x.exception.type !== "lost" && <button onClick={() => markLost(x)} data-testid={`ex-lost-${x.id}`} className="h-6 px-1.5 border border-border hover:bg-muted text-[10px] font-bold uppercase inline-flex items-center gap-1"><Ghost className="h-3 w-3" /> Lost</button>}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Panel>
          ) : (
            <Panel testid="claims-table" bodyClass="overflow-x-auto">
              <table className="w-full text-xs">
                <thead className={TH_ROW}>
                  <tr>
                    <SortTh k="_no" label="Claim #" sort={cs} />
                    <SortTh k="awb" label="AWB" sort={cs} />
                    <SortTh k="merchant_name" label="Merchant" sort={cs} />
                    <SortTh k="carrier_name" label="Carrier" sort={cs} />
                    <th className="px-3 py-2 text-left">Reason</th>
                    <SortTh k="_amt" label="Claimed" sort={cs} align="right" />
                    <SortTh k="_settled" label="Settled" sort={cs} align="right" />
                    <SortTh k="_filed" label="Filed" sort={cs} />
                    <SortTh k="_status" label="Status" sort={cs} />
                    <th className="px-3 py-2 text-left">Update</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {cs.sorted.length === 0 && <tr><td colSpan={10} className="p-8 text-center text-muted-foreground">No claims yet.</td></tr>}
                  {cs.sorted.map((c) => (
                    <tr key={c.claim.id} className="hover:bg-muted/40" data-testid={`claim-row-${c.claim.claim_no}`}>
                      <td className={`${TD} font-mono-num font-bold`}>{c.claim.claim_no}</td>
                      <td className={TD}><button onClick={() => setDetailId(c.id)} className="font-mono-num hover:text-primary">{c.awb}</button></td>
                      <td className={`${TD} max-w-[140px] truncate`}>{c.merchant_name}</td>
                      <td className={TD}>{c.carrier_name}<div className="text-[10px] text-muted-foreground">{c.claim.insurer || "Carrier liability"}</div></td>
                      <td className="px-3 py-2 text-[11px] text-muted-foreground max-w-[220px] truncate" title={c.claim.reason}>{c.claim.reason}</td>
                      <td className={`${TD} text-right font-mono-num`}>{inr(c.claim.amount)}</td>
                      <td className={`${TD} text-right font-mono-num`}>{c.claim.settled_amount ? inr(c.claim.settled_amount) : "—"}</td>
                      <td className={TD}>{fmtDate(c.claim.filed_at)}</td>
                      <td className={TD}><Pill className={CLAIM_STYLE[c.claim.status]}>{CLAIM_LABEL[c.claim.status]}</Pill></td>
                      <td className={TD}>
                        <FieldSelect testid={`claim-status-${c.claim.claim_no}`} value={c.claim.status} onChange={(v) => updClaim(c, v)} className="h-7 text-xs">
                          {Object.entries(CLAIM_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
                        </FieldSelect>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Panel>
          )}
        </>
      )}
      <RerouteDialog item={rr} meta={meta} onClose={() => setRr(null)} onDone={refresh} />
      <ClaimDialog item={cl} onClose={() => setCl(null)} onDone={refresh} />
      <ShipmentDetailSheet id={detailId} onOpenChange={(o) => !o && setDetailId(null)} onChanged={refresh} />
    </div>
  );
}
